import React, { useEffect, useMemo, useState } from 'react';
import { CheckRounded } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import signupFlowSteps from './signupFlowConfig';
import SignupProgress from './signupProgress';
import { signupStep4Schema } from './validationSchemas';
import { subscriptionMetaData } from './login.service';
import { normalizePlan } from './signupPlanCatalog';
import { authHeadingStyles, authSubtextStyles } from './signupTheme';
import { useSignupMetaData } from '../hooks/useSignupMetaData';

const fieldLabelStyles = {
  color: 'text.primary',
  fontWeight: 700,
};


export default function SignupStep4({
  handleContinue,
  handleBack,
  handleChange,
  handleOpenFinancialSupport,
  formData,
}) {
  const { userTypes } = useSignupMetaData(formData.administrativeRegion);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [planOptions, setPlanOptions] = useState([]);

  const selectedUserType = useMemo(
    () =>
      (userTypes || []).find(
        (userType) => String(userType.id) === String(formData.userTypeId)
      ) || null,
    [userTypes, formData.userTypeId]
  );

  const isBankUserType = selectedUserType?.typeName === 'BANK';

  useEffect(() => {
    const loadSubscriptionPlans = async () => {
      try {
        const response = await subscriptionMetaData(
          formData.userTypeId,
          formData.administrativeRegion
        );

        if (response?.statusCode === 200 || response?.statusCode === 201) {
          const rawPlans = Array.isArray(response?.data)
            ? response.data
            : response?.data?.subscriptionPlans || response?.data?.plans || [];

          if (rawPlans.length > 0) {
            setPlanOptions(rawPlans.map(normalizePlan));
            return;
          }
        }
      } catch (error) {
        // Set empty if API call fails
      }

      setPlanOptions([]);
    };

    loadSubscriptionPlans();
  }, [formData.administrativeRegion, formData.userTypeId]);

  const selectedBillingCycle = formData.subscriptionBillingCycle || 'monthly';

  const availablePlans = useMemo(
    () => planOptions,
    [planOptions]
  );

  const validateField = async (name, value) => {
    try {
      await signupStep4Schema.validateAt(name, {
        ...formData,
        [name]: value,
      });
      setErrors((currentErrors) => {
        if (!currentErrors[name]) {
          return currentErrors;
        }

        const nextErrors = { ...currentErrors };
        delete nextErrors[name];
        return nextErrors;
      });
    } catch (error) {
      setErrors((currentErrors) => ({
        ...currentErrors,
        [name]: error.message,
      }));
    }
  };

  const validateStep = async () => {
    try {
      await signupStep4Schema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (validationError) {
      const nextErrors = {};
      validationError.inner.forEach((error) => {
        if (!nextErrors[error.path]) {
          nextErrors[error.path] = error.message;
        }
      });
      setErrors(nextErrors);
      return false;
    }
  };

  const handleBillingCycleChange = (_, nextCycle) => {
    if (!nextCycle) {
      return;
    }

    handleChange({
      target: {
        name: 'subscriptionBillingCycle',
        value: nextCycle,
      },
    });
  };

  const handlePlanSelect = async (planId) => {
    const selectedPlan = availablePlans.find(
      (plan) => String(plan.id) === String(planId)
    );

    setTouched((currentTouched) => ({
      ...currentTouched,
      subscriptionPlanId: true,
    }));

    handleChange({
      target: {
        name: 'subscriptionPlanId',
        value: planId,
      },
    });
    handleChange({
      target: {
        name: 'subscriptionPlanName',
        value: selectedPlan?.planName || '',
      },
    });
    handleChange({
      target: {
        name: 'subscriptionPlanCurrency',
        value: selectedPlan?.displayCurrency || '$',
      },
    });
    handleChange({
      target: {
        name: 'isCustomPlanRequest',
        type: 'checkbox',
        checked: selectedPlan?.isCustom || false,
      },
    });
    handleChange({
      target: {
        name: 'reviewConfirmed',
        type: 'checkbox',
        checked: false,
      },
    });

    // Store financial support details from selected plan
    const financialSupportDetails = selectedPlan?.financialSupportDetails || [];
    handleChange({
      target: {
        name: 'selectedPlanFinancialSupportDetails',
        value: financialSupportDetails,
      },
    });

    // Store plan pricing information for financial support calculations
    handleChange({
      target: {
        name: 'subscriptionPlanMonthlyPrice',
        value: selectedPlan?.monthlyPrice || 0,
      },
    });
    handleChange({
      target: {
        name: 'subscriptionPlanYearlyPrice',
        value: selectedPlan?.yearlyPrice || 0,
      },
    });
    handleChange({
      target: {
        name: 'subscriptionPlanSetupFee',
        value: selectedPlan?.setupFee || 0,
      },
    });
    handleChange({
      target: {
        name: 'subscriptionPlanYearlySetupFee',
        value: selectedPlan?.yearlySetupFee || 0,
      },
    });

    await validateField('subscriptionPlanId', planId);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setTouched({
      subscriptionPlanId: true,
    });

    const isValid = await validateStep();
    if (isValid) {
      handleContinue();
    }
  };

  const renderFieldLabel = (label, required = false) => (
    <Typography variant="subtitle2" sx={fieldLabelStyles}>
      {label}
      {required ? (
        <Box component="span" sx={{ color: 'error.main' }}>
          {' '}
          *
        </Box>
      ) : null}
    </Typography>
  );

  return (
    <Stack spacing={4} sx={{ maxWidth: 1160, mx: 'auto' }}>
      <Box sx={{ width: '100%', px: { xs: 0, md: 1 }, pb: 1 }}>
        <SignupProgress steps={signupFlowSteps} activeStep={3} />
      </Box>

      <Paper
        elevation={0}
        sx={{
          px: { xs: 2.5, sm: 6, md: 8 },
          py: { xs: 2.5, sm: 6, md: 8 },
          borderRadius: 4,
          backgroundColor: '#ffffff',
          boxShadow: '0 18px 48px rgba(28, 27, 31, 0.10)',
          border: '1px solid #E4E6F1',
        }}
      >
        <Stack spacing={3}>
          <Stack spacing={1} alignItems="center" textAlign="center">
            <Typography
              variant="h4"
              sx={authHeadingStyles}
            >
              Choose Your Plan
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={authSubtextStyles}
            >
              Select the subscription plan that fits your needs.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              To proceed without financial support application, select your desired
              plan below and click "continue" button.
            </Typography>
          </Stack>

          {!availablePlans.some(p => p.id === formData.subscriptionPlanId && p.isCustom) && (
            <Stack spacing={1.5} alignItems="center">
              <ToggleButtonGroup
                exclusive
                value={selectedBillingCycle}
                onChange={handleBillingCycleChange}
                color="primary"
                sx={{
                  backgroundColor: 'rgba(90, 143, 17, 0.05)',
                  borderRadius: 999,
                  p: 0.5,
                  '& .MuiToggleButton-root': {
                    border: 'none',
                    px: 2.5,
                    py: 1,
                    borderRadius: 999,
                    color: 'text.secondary',
                    textTransform: 'none',
                    fontWeight: 700,
                  },
                  '& .Mui-selected': {
                    backgroundColor: '#ffffff !important',
                    color: 'text.primary !important',
                  },
                }}
              >
                <ToggleButton value="monthly">Billed monthly</ToggleButton>
                <ToggleButton value="yearly">Billed yearly</ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700 }}>
                Save 20% with yearly billing
              </Typography>
            </Stack>
          )}

          <Stack spacing={1}>
            {renderFieldLabel('Select Subscription Plan', true)}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(3, minmax(0, 1fr))',
                },
                gap: 2,
                alignItems: 'stretch',
              }}
            >
              {availablePlans.map((plan) => {
                const isSelected = String(formData.subscriptionPlanId) === String(plan.id);
                const planPrice =
                  selectedBillingCycle === 'yearly' && plan.yearlyPrice !== null
                    ? plan.yearlyPrice
                    : plan.monthlyPrice;
                const setupFee =
                  selectedBillingCycle === 'yearly' ? plan.yearlySetupFee : plan.setupFee;
                const priceSuffix =
                  selectedBillingCycle === 'yearly'
                    ? '/year'
                    : '/mo';
                const setupFeeLabel =
                  selectedBillingCycle === 'yearly'
                    ? 'one time yearly payment'
                    : 'one time setup fee';

                if (plan.isCustom) {
                  return (
                    <Paper
                      key={plan.id}
                      elevation={0}
                      onClick={() => handlePlanSelect(plan.id)}
                      sx={{
                        borderRadius: 3,
                        border: `2px solid ${isSelected ? '#A5C86B' : '#E4E6F1'}`,
                        background: isSelected
                          ? 'linear-gradient(180deg, rgba(30, 42, 166, 0.05) 0%, rgba(30, 42, 166, 0.02) 100%)'
                          : '#ffffff',
                        px: { xs: 2.5, sm: 3 },
                        py: { xs: 3, sm: 3.5 },
                        minHeight: { xs: 'auto', md: 364 },
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: 'pointer',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                      }}
                    >
                      <Stack spacing={2} sx={{ height: '100%', justifyContent: 'center', alignItems: 'center' }}>
                        <Chip
                          label="Custom Plan"
                          sx={{
                            backgroundColor: 'rgba(30, 42, 166, 0.08)',
                            color: '#1E2AA6',
                            fontWeight: 800,
                            textTransform: 'uppercase',
                          }}
                        />
                        <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary' }}>
                          {plan.planName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {plan.description || 'Contact us for custom pricing and features'}
                        </Typography>
                        <Typography variant="body2" color="primary.main" sx={{ fontWeight: 700, mt: 2 }}>
                          {isSelected ? '✓ Selected' : 'Click to select'}
                        </Typography>
                      </Stack>
                    </Paper>
                  );
                }

                return (
                  <Paper
                    key={plan.id}
                    elevation={0}
                    onClick={() => handlePlanSelect(plan.id)}
                    sx={{
                      borderRadius: 3,
                      border: `1px solid ${isSelected ? '#A5C86B' : '#E4E6F1'}`,
                      background: isSelected
                        ? 'linear-gradient(180deg, rgba(245, 252, 235, 0.95) 0%, rgba(248, 251, 242, 0.85) 100%)'
                        : '#ffffff',
                      px: { xs: 2.5, sm: 3 },
                      py: { xs: 3, sm: 3.5 },
                      minHeight: { xs: 'auto', md: 364 },
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                    }}
                  >
                    <Stack spacing={2} sx={{ height: '100%' }}>
                      <Stack spacing={1} alignItems="center" textAlign="center">
                        <Chip
                          label={plan.planName}
                          sx={{
                            backgroundColor: 'rgba(90, 143, 17, 0.08)',
                            color: 'primary.main',
                            fontWeight: 800,
                            textTransform: 'uppercase',
                          }}
                        />
                        <Stack spacing={0.5} alignItems="center">
                          <Typography
                            sx={{
                              color: 'primary.main',
                              fontSize: 44,
                              fontWeight: 800,
                              lineHeight: 1,
                            }}
                          >
                            {plan.displayCurrency}
                            {planPrice}
                            <Typography
                              component="span"
                              sx={{
                                fontSize: 20,
                                fontWeight: 700,
                                color: 'primary.main',
                              }}
                            >
                              {priceSuffix}
                            </Typography>
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            +{' '}
                            <Box
                              component="span"
                              sx={{ fontWeight: 700, color: 'text.primary' }}
                            >
                              {setupFeeLabel} {plan.displayCurrency}
                              {setupFee}
                            </Box>
                          </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {plan.description || `${plan.planName} plan description...`}
                        </Typography>
                      </Stack>

                      <Stack spacing={1.25} sx={{ mt: 1 }}>
                        {plan.features.map((feature) => (
                          <Stack
                            key={`${plan.id}-${feature}`}
                            direction="row"
                            spacing={1.25}
                            alignItems="center"
                          >
                            <CheckRounded sx={{ color: 'primary.main', fontSize: 18 }} />
                            <Typography variant="body2" color="text.primary">
                              {feature}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>

                      {plan.customizationNote ? (
                        <Stack
                          spacing={1.25}
                          sx={{
                            mt: 'auto',
                            pt: 2,
                            borderTop: '1px solid #E4E6F1',
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Customization
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {plan.customizationNote}
                          </Typography>
                          <Button variant="contained" size="medium">
                            Contact Us
                          </Button>
                        </Stack>
                      ) : null}
                    </Stack>
                  </Paper>
                );
              })}
            </Box>
            {touched.subscriptionPlanId && errors.subscriptionPlanId ? (
              <Typography variant="caption" color="error.main">
                {errors.subscriptionPlanId}
              </Typography>
            ) : null}
          </Stack>

          {!isBankUserType && (
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 3,
                borderColor: '#E4E6F1',
                px: { xs: 2, sm: 3 },
                py: 2.5,
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', md: 'center' }}
              >
                <Stack spacing={0.5}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Need financial support?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    If it would help you, choose your desired plan above and open the
                    financial support application.
                  </Typography>
                  {formData.financialSupportRequested && formData.financialSupportPlanName ? (
                    <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700 }}>
                      Applied: {formData.financialSupportPlanName}{' '}
                      <Box
                        component="span"
                        onClick={() => {
                          handleChange({
                            target: {
                              name: 'financialSupportRequested',
                              type: 'checkbox',
                              checked: false,
                            },
                          });
                          handleChange({
                            target: {
                              name: 'financialSupportType',
                              value: '',
                            },
                          });
                          handleChange({
                            target: {
                              name: 'financialSupportPlanId',
                              value: '',
                            },
                          });
                          handleChange({
                            target: {
                              name: 'financialSupportPlanName',
                              value: '',
                            },
                          });
                        }}
                        sx={{
                          cursor: 'pointer',
                          color: '#d32f2f',
                        }}
                      >
                        (Cancel Application)
                      </Box>
                    </Typography>
                  ) : null}
                </Stack>

                <Stack spacing={1} sx={{ minWidth: 164, width: { xs: '100%', md: 'auto' } }}>
                  <Button
                    variant={formData.financialSupportRequested ? 'contained' : 'outlined'}
                    onClick={handleOpenFinancialSupport}
                    disabled={!formData.subscriptionPlanId}
                    sx={{
                      minWidth: 164,
                      width: '100%',
                      borderColor: '#1E2AA6',
                      color: formData.financialSupportRequested ? '#ffffff' : '#1E2AA6',
                      backgroundColor: formData.financialSupportRequested
                        ? '#1E2AA6'
                        : '#ffffff',
                      '&:hover': {
                        borderColor: '#1E2AA6',
                        backgroundColor: formData.financialSupportRequested
                          ? '#17208c'
                          : 'rgba(30, 42, 166, 0.04)',
                      },
                      '&:disabled': {
                        borderColor: '#ccc',
                        color: '#999',
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  >
                    {formData.financialSupportRequested ? 'Edit Application' : 'Apply'}
                  </Button>
                  {!formData.subscriptionPlanId && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                      Select a subscription plan first to apply for financial support
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </Paper>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
              pt: 1,
            }}
          >
              <Button
                variant="outlined"
                size="large"
                onClick={handleBack}
                sx={{
                  order: { xs: 2, sm: 1 },
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'rgba(90, 143, 17, 0.06)',
                },
              }}
            >
              Back
              </Button>
              <Button
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={!formData.subscriptionPlanId}
                sx={{ order: { xs: 1, sm: 2 } }}
              >
                Continue
              </Button>
          </Box>
        </Stack>
      </Paper>
    </Stack>
  );
}
